import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProgressBar } from "@/shared/components/progress-bar";
import { TurnstileWidget, turnstileRequired } from "@/shared/components/turnstile-widget";
import { cn } from "@/shared/lib/utils";
import { Link } from "@tanstack/react-router";
import type { HcaDiagnozaScores } from "@template/shared";
import { ArrowLeft, ArrowRight, Calendar, CheckCircle2, Compass, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ScoreSlider } from "./score-slider";

type Area = keyof HcaDiagnozaScores;

const AREAS: readonly {
  key: Area;
  title: string;
  question: string;
}[] = [
  {
    key: "komunikace",
    title: "Komunikace",
    question: "Jak spolehlivě se ve firmě předávají rozhodnutí, priority a odpovědnosti?",
  },
  {
    key: "obchod",
    title: "Obchod",
    question: "Jak předvídatelně funguje obchodní pipeline, akvizice a dotahování zakázek?",
  },
  {
    key: "leadership",
    title: "Leadership",
    question: "Jak silná je manažerská vrstva pod majitelem nebo jednatelem?",
  },
  {
    key: "management",
    title: "Management",
    question: "Jak dobře jsou nastavené porady, cíle, kontrola plnění a každodenní řízení?",
  },
  {
    key: "operativa",
    title: "Operativa",
    question: "Jak plynule běží provoz bez toho, aby vedení muselo stále hasit operativu?",
  },
  {
    key: "strategie",
    title: "Strategie",
    question: "Jak jasně má vedení pojmenované priority, směr a odpovědnosti na další období?",
  },
  {
    key: "finance",
    title: "Finance",
    question: "Jak dobře vedení vidí marži, cash-flow, náklady a dopad rozhodnutí na výsledek?",
  },
];

type WizardStep =
  | { kind: "intro" }
  | { kind: "area"; index: number }
  | { kind: "contact" }
  | { kind: "result" };

interface DiagnozaWizardProps {
  variant?: "page" | "modal";
  /** Voláno při kliknutí na konverzní CTA ve výsledku — v modal variantě zavře dialog. */
  onNavigate?: () => void;
}

/** 9-krokový wizard: intro → 7 oblastí → kontakt → výsledek. */
export function DiagnozaWizard({ variant = "page", onNavigate }: DiagnozaWizardProps = {}) {
  const [step, setStep] = useState<WizardStep>({ kind: "intro" });
  const [scores, setScores] = useState<HcaDiagnozaScores>({
    komunikace: 50,
    obchod: 50,
    leadership: 50,
    management: 50,
    operativa: 50,
    strategie: 50,
    finance: 50,
  });
  const [email, setEmail] = useState("");
  const [telefon, setTelefon] = useState("");
  const [jmeno, setJmeno] = useState("");
  const [firma, setFirma] = useState("");
  const [ico, setIco] = useState("");
  const [consent, setConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<{
    totalScore: number;
    weakestArea: Area;
    recommendedProduct: {
      id: string;
      nazev: string;
      slug: string;
      kategorie: string;
    } | null;
  } | null>(null);

  const submitMutation = trpc.hca.leadMagnet.submit.useMutation({
    onSuccess: (data) => {
      setResult({
        totalScore: data.totalScore,
        weakestArea: data.weakestArea,
        recommendedProduct: data.recommendedProduct,
      });
      setStep({ kind: "result" });
    },
    onError: (err) => toast.error(err.message),
  });

  const updateScore = (key: Area, val: number) => {
    setScores((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = () => {
    if (!consent) {
      toast.error("Pro odeslání je nutný souhlas se zpracováním údajů");
      return;
    }
    if (!email) {
      toast.error("E-mail je povinný");
      return;
    }
    // Stejná pravidla jako server (submitDiagnozaSchema) — ať uživatel nedostane
    // matoucí server error až po odeslání.
    const telefonTrimmed = telefon.trim();
    if (telefonTrimmed === "") {
      toast.error("Telefon je povinný");
      return;
    }
    if (
      (telefonTrimmed.match(/\d/g)?.length ?? 0) < 6 ||
      !/^[+\d(][\d\s\-()/]*$/.test(telefonTrimmed)
    ) {
      toast.error("Zadejte platný telefon (alespoň 6 číslic, bez písmen)");
      return;
    }

    submitMutation.mutate({
      email,
      telefon: telefon.trim(),
      scores,
      consent: true,
      ...(jmeno.trim() ? { jmeno: jmeno.trim() } : {}),
      ...(firma.trim() ? { firma: firma.trim() } : {}),
      ...(ico.trim().length === 8 ? { ico: ico.trim() } : {}),
      ...(turnstileToken ? { turnstileToken } : {}),
    });
  };

  const totalSteps = 2 + AREAS.length + 1; // intro + areas + contact + result
  const currentStepIdx = (() => {
    if (step.kind === "intro") return 0;
    if (step.kind === "area") return 1 + step.index;
    if (step.kind === "contact") return 1 + AREAS.length;
    return totalSteps - 1;
  })();

  const isModal = variant === "modal";
  // `min-h-0` na obou flex-1 úrovních (Card i CardContent) je nutné, aby se
  // overflow-y-auto v modálu skutečně uplatnil — jinak vysoký krok (kontakt)
  // přeteče a spodní lišta s tlačítkem „Odeslat" zmizí pod hranou dialogu.
  const cardClass = isModal
    ? "flex min-h-0 flex-1 flex-col overflow-hidden border-0 bg-transparent shadow-none"
    : "";
  const headerClass = isModal ? "px-6 pt-6 pb-2" : "";
  const contentClass = isModal ? "flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-4 pt-2" : "";
  const navFooterClass = isModal
    ? "-mx-6 -mb-4 mt-auto flex flex-col-reverse gap-2 border-t border-border bg-background px-6 py-4 sm:flex-row sm:justify-between"
    : "flex flex-col-reverse gap-2 sm:flex-row sm:justify-between";

  return (
    <div
      className={cn(
        "mx-auto w-full",
        isModal
          ? "flex h-full max-w-full flex-col overflow-hidden"
          : "flex max-w-2xl flex-col min-h-screen px-4 py-8 justify-center",
      )}
      data-testid="hca-diagnoza-wizard"
    >
      <div
        className={cn(isModal ? "border-b border-border px-6 py-3 pr-14" : "mb-6")}
        data-testid="hca-diagnoza-progress"
      >
        <ProgressBar
          value={currentStepIdx + 1}
          max={totalSteps}
          label={`Krok ${currentStepIdx + 1} z ${totalSteps}`}
        />
      </div>

      {step.kind === "intro" ? (
        <Card className={cardClass} data-testid="hca-diagnoza-step-intro">
          <CardHeader className={headerClass}>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Compass className="h-6 w-6" aria-hidden="true" />
            </div>
            <CardTitle className="text-2xl">Manažerská diagnostika firmy</CardTitle>
            <p className="mt-2 text-muted-foreground">
              Odpovězte na 7 krátkých otázek a zjistěte, kde má vaše firma největší rezervu v
              řízení, obchodu nebo výkonu týmu. Dostanete doporučení konkrétního HCA programu.
            </p>
          </CardHeader>
          <CardContent className={contentClass}>
            <div className={navFooterClass}>
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={() => setStep({ kind: "area", index: 0 })}
                data-testid="hca-diagnoza-start-button"
              >
                Spustit diagnostiku
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step.kind === "area"
        ? (() => {
            const area = AREAS[step.index];
            if (!area) return null;
            const isLast = step.index === AREAS.length - 1;
            return (
              <Card className={cardClass} data-testid={`hca-diagnoza-step-area-${step.index}`}>
                <CardHeader className={headerClass}>
                  <CardTitle className="text-xl">
                    {step.index + 1}. {area.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className={cn(contentClass, "space-y-6")}>
                  <ScoreSlider
                    id={area.key}
                    label={area.question}
                    value={scores[area.key]}
                    onChange={(v) => updateScore(area.key, v)}
                  />

                  <div className={navFooterClass}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (step.index === 0) setStep({ kind: "intro" });
                        else setStep({ kind: "area", index: step.index - 1 });
                      }}
                      data-testid={`hca-diagnoza-back-${step.index}`}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                      Zpět
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (isLast) setStep({ kind: "contact" });
                        else setStep({ kind: "area", index: step.index + 1 });
                      }}
                      data-testid={`hca-diagnoza-next-${step.index}`}
                    >
                      {isLast ? "Pokračovat" : "Další"}
                      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()
        : null}

      {step.kind === "contact" ? (
        <Card className={cardClass} data-testid="hca-diagnoza-step-contact">
          <CardHeader className={headerClass}>
            <CardTitle className="text-xl">Vaše kontaktní údaje</CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              Výsledek uvidíte hned po odeslání. Údaje slouží pro doporučení vhodného programu a
              navázání kontaktu, nikomu je nepředáváme.
            </p>
          </CardHeader>
          <CardContent className={cn(contentClass, "space-y-4")}>
            <div className="space-y-2">
              <Label htmlFor="diag-email">E-mail</Label>
              <Input
                id="diag-email"
                type="email"
                placeholder="vase@firma.cz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="hca-diagnoza-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="diag-telefon">Telefon</Label>
              <Input
                id="diag-telefon"
                type="tel"
                inputMode="tel"
                aria-required="true"
                placeholder="+420 603 123 456"
                maxLength={32}
                value={telefon}
                onChange={(e) => setTelefon(e.target.value)}
                data-testid="hca-diagnoza-telefon-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="diag-jmeno">Jméno (volitelné)</Label>
                <Input
                  id="diag-jmeno"
                  value={jmeno}
                  onChange={(e) => setJmeno(e.target.value)}
                  maxLength={255}
                  data-testid="hca-diagnoza-jmeno-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="diag-firma">Firma (volitelné)</Label>
                <Input
                  id="diag-firma"
                  value={firma}
                  onChange={(e) => setFirma(e.target.value)}
                  maxLength={255}
                  data-testid="hca-diagnoza-firma-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="diag-ico">IČO (volitelné)</Label>
              <Input
                id="diag-ico"
                inputMode="numeric"
                maxLength={8}
                value={ico}
                onChange={(e) => setIco(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="12345678"
                data-testid="hca-diagnoza-ico-input"
              />
            </div>

            <label
              htmlFor="diag-consent"
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3"
            >
              <input
                id="diag-consent"
                type="checkbox"
                className="mt-1 h-5 w-5"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="hca-diagnoza-consent-checkbox"
              />
              <span className="text-sm">
                Souhlasím se zpracováním e-mailu a telefonu pro doporučení vhodného programu a
                navázání kontaktu. Více v{" "}
                <a
                  href="/gdpr"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                  data-testid="hca-diagnoza-gdpr-link"
                >
                  GDPR dokumentu
                </a>
                .
              </span>
            </label>

            <TurnstileWidget onToken={setTurnstileToken} testId="hca-diagnoza-turnstile" />

            <div className={navFooterClass}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep({ kind: "area", index: AREAS.length - 1 })}
                disabled={submitMutation.isPending}
                data-testid="hca-diagnoza-contact-back"
              >
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                Zpět
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={
                  !consent || submitMutation.isPending || (turnstileRequired && !turnstileToken)
                }
                data-testid="hca-diagnoza-submit"
              >
                {submitMutation.isPending ? "Odesílám…" : "Odeslat a zobrazit výsledky"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step.kind === "result" && result ? (
        <Card className={cardClass} data-testid="hca-diagnoza-step-result">
          <CardHeader className={headerClass}>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
            </div>
            <CardTitle className="text-2xl">Výsledek diagnostiky</CardTitle>
          </CardHeader>
          <CardContent className={cn(contentClass, "space-y-4")}>
            <div
              className="rounded-lg border border-border p-4 text-center"
              data-testid="hca-diagnoza-result-total"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Celkové skóre
              </div>
              <div className="mt-1 text-5xl font-bold tabular-nums">
                {result.totalScore}
                <span className="ml-1 text-lg font-normal text-muted-foreground">/100</span>
              </div>
            </div>

            <div
              className="rounded-lg border border-border p-4"
              data-testid="hca-diagnoza-result-weakest"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Největší prostor k růstu
              </div>
              <div className="mt-1 text-xl font-semibold">
                {AREAS.find((a) => a.key === result.weakestArea)?.title ?? result.weakestArea}
              </div>
            </div>

            {result.recommendedProduct ? (
              <div
                className="rounded-lg border border-primary bg-primary/5 p-4"
                data-testid="hca-diagnoza-result-product"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Doporučený program
                  </div>
                </div>
                <div className="mt-1 text-xl font-semibold">{result.recommendedProduct.nazev}</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Kategorie: {result.recommendedProduct.kategorie}
                </p>
              </div>
            ) : null}

            <ul className="space-y-2" data-testid="hca-diagnoza-result-scores">
              {AREAS.map((area) => (
                <li key={area.key} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{area.title}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {scores[area.key]}/100
                    </span>
                  </div>
                  <ProgressBar value={scores[area.key]} max={100} showCount={false} />
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row">
              {result.recommendedProduct ? (
                <Button
                  asChild
                  className="w-full flex-1"
                  onClick={onNavigate}
                  data-testid="hca-diagnoza-result-product-cta"
                >
                  <Link to="/seminare/$slug" params={{ slug: result.recommendedProduct.slug }}>
                    <Sparkles className="h-4 w-4" aria-hidden="true" />
                    Zobrazit doporučený program
                  </Link>
                </Button>
              ) : null}
              <Button
                asChild
                variant="outline"
                className="w-full flex-1"
                onClick={onNavigate}
                data-testid="hca-diagnoza-result-terminy-cta"
              >
                <Link to="/terminy">
                  <Calendar className="h-4 w-4" aria-hidden="true" />
                  Vybrat termín
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
